import React, { useState } from "react";
import { Typography, Box, Avatar, Chip, IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { _date, formatMoney } from "../../../utils/helpers";

const STUDENT_AVATAR_SIZE = 30;
const COURSE_AVATAR_SIZE = 30;

function getCourseName(row) {
    return row.course_name_en || row.course_name_ru || row.course_name_hy || "—";
}

function getUserName(row) {
    const first = (row.user_first_name ?? "").trim();
    const last = (row.user_last_name ?? "").trim();
    const full = `${first} ${last}`.trim();
    return full || "—";
}

function getStatusColor(status) {
    const s = status ? String(status).toLowerCase() : "";
    switch (s) {
        case "processed":
            return "success";
        case "pending":
            return "warning";
        case "expired":
            return "default";
        case "canceled":
            return "error";
        default:
            return "default";
    }
}

function getStatusLabel(status) {
    if (!status) return "—";
    const s = String(status);
    return s.charAt(0).toUpperCase() + s.slice(1);
}

function ActionsCell({ row, onAssignClassroom, onAdjustLessons, onChangeStatus, onDeletePurchase, onRemoveFromClassroom }) {
    const [anchorEl, setAnchorEl] = useState(null);
    const open = Boolean(anchorEl);

    return (
        <>
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
                <MoreVertIcon fontSize="small" />
            </IconButton>
            <Menu anchorEl={anchorEl} open={open} onClose={() => setAnchorEl(null)}>
                <MenuItem
                    onClick={() => {
                        setAnchorEl(null);
                        onAssignClassroom(row);
                    }}
                >
                    {row.classroom_id ? "Change classroom" : "Assign classroom"}
                </MenuItem>
                {row.classroom_id && typeof onRemoveFromClassroom === "function" ? (
                    <MenuItem
                        onClick={() => {
                            setAnchorEl(null);
                            onRemoveFromClassroom(row);
                        }}
                    >
                        Remove from classroom
                    </MenuItem>
                ) : null}
                <MenuItem
                    onClick={() => {
                        setAnchorEl(null);
                        onAdjustLessons(row);
                    }}
                >
                    Adjust lessons
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setAnchorEl(null);
                        onChangeStatus(row);
                    }}
                >
                    Change status
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        setAnchorEl(null);
                        onDeletePurchase(row);
                    }}
                >
                    Delete purchase
                </MenuItem>
            </Menu>
        </>
    );
}

export const PurchasedCourseColumns = ({ onAssignClassroom, onAdjustLessons, onChangeStatus, onDeletePurchase, onRemoveFromClassroom }) => [
    {
        id: "user_first_name",
        label: "Student",
        render: (value, row) => {
            const userName = getUserName(row);
            const email = (row.user_email ?? "").trim();
            return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                        variant="rounded"
                        src={row.user_avatar || undefined}
                        alt={userName}
                        sx={{ width: STUDENT_AVATAR_SIZE, height: STUDENT_AVATAR_SIZE, fontSize: 12, flexShrink: 0 }}
                    >
                        {userName.slice(0, 1)}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
                            {userName}
                        </Typography>
                        {email ? (
                            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", lineHeight: 1.3 }}>
                                {email}
                            </Typography>
                        ) : null}
                    </Box>
                </Box>
            );
        },
    },
    {
        id: "course_thumbnail",
        label: "Course",
        render: (value, row) => {
            const courseName = getCourseName(row);
            return (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Avatar
                        variant="rounded"
                        src={row.course_thumbnail || undefined}
                        alt={courseName}
                        sx={{ width: COURSE_AVATAR_SIZE, height: COURSE_AVATAR_SIZE, fontSize: 12, flexShrink: 0 }}
                    >
                        {courseName.slice(0, 1)}
                    </Avatar>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {courseName}
                    </Typography>
                </Box>
            );
        },
    },
    {
        id: "classroom_name",
        label: "Classroom",
        render: (value, row) => {
            const name = row.classroom_name?.trim();
            return (
                <Typography variant="body2" sx={{ color: name ? "text.primary" : "text.secondary", textAlign: "center" }}>
                    {name || "Not assigned"}
                </Typography>
            );
        },
    },
    {
        id: "status",
        label: "Status",
        render: (value, row) => {
            return (
                <Box sx={{ textAlign: "center" }}>
                    <Chip
                        label={getStatusLabel(row.status)}
                        size="small"
                        color={getStatusColor(row.status)}
                    />
                </Box>
            );
        },
    },
    {
        id: "price",
        label: "Price",
        render: (value, row) => {
            const numeric = Number(row.price);
            const safe = Number.isFinite(numeric) ? numeric : 0;
            return (
                <Box sx={{ textAlign: "center" }}>
                    <Chip
                        label={formatMoney(safe)}
                        size="small"
                        color="success"
                        variant="outlined"
                    />
                </Box>
            );
        },
    },
    {
        id: "lesson_amount",
        label: "Total lessons",
        render: (value, row) => {
            const lessons = row.lesson_amount ?? 0;
            return (
                <Typography variant="body2" sx={{ textAlign: "center" }}>
                    {Number(lessons).toFixed(0)}
                </Typography>
            );
        },
    },
    {
        id: "remaining_lessons",
        label: "Remaining lessons",
        render: (value, row) => {
            return <Typography variant="body2" sx={{ textAlign: "center" }}>{row.remaining_lessons ?? 0}</Typography>;
        },
    },
    {
        id: "expire_at",
        label: "Expire at",
        render: (value, row) => {
            return (
                <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center" }}>
                    {_date(row.expire_at, { relative: false, long: false })}
                </Typography>
            );
        },
    },
    ...(typeof onAssignClassroom === "function" && typeof onAdjustLessons === "function"
        ? [
              {
                  id: "actions",
                  label: "Actions",
                  render: (value, row) => (
                      <Box sx={{ display: "flex", justifyContent: "center" }}>
                          <ActionsCell
                              row={row}
                              onAssignClassroom={onAssignClassroom}
                              onAdjustLessons={onAdjustLessons}
                              onChangeStatus={onChangeStatus}
                              onDeletePurchase={onDeletePurchase}
                              onRemoveFromClassroom={onRemoveFromClassroom}
                          />
                      </Box>
                  ),
              },
          ]
        : []),
];
